"use client"

import Image from "next/image"
import { useMemo, useState, useEffect } from "react"

import { X, ChevronLeft, ChevronRight } from "lucide-react"

type GalleryItem = {
  id: string
  title: string
  category: string
  image: string
}

type GalleryWithFiltersProps = {
  heading?: string
  categories?: string[]
  items?: GalleryItem[]
  galleryType?: 'school' | 'college' | 'all'
}

const STATIC_GALLERY_ITEMS: GalleryItem[] = [
  { id: "2", title: "Modern Classroom", category: "Academics", image: "/images/Class-room.webp" },
  { id: "3", title: "Computer Laboratory", category: "Facilities", image: "/images/computerlab.webp" },
  { id: "4", title: "Science Lab", category: "Academics", image: "/images/sci_lab.webp" },
  { id: "5", title: "School Library", category: "Facilities", image: "/images/library.webp" },
  { id: "6", title: "Sports Activity", category: "Sports", image: "/images/sports.webp" },
  { id: "7", title: "Student Council", category: "Events", image: "/images/student-council.webp" },
  { id: "8", title: "Playground Area", category: "Campus", image: "/images/playground.webp" },
  { id: "9", title: "Annual Event", category: "Events", image: "/images/gallery1.webp" },
  { id: "10", title: "Cultural Program", category: "Events", image: "/images/gallery2.webp" },
  { id: "11", title: "Prize Distribution", category: "Events", image: "/images/gallery3.webp" },
  { id: "12", title: "Morning Assembly", category: "Sports", image: "/images/gallery4.webp" },
  { id: "13", title: "Lab Sessions", category: "Academics", image: "/images/gallery5.webp" },
  { id: "14", title: "Yoga Session", category: "Sports", image: "/images/gallery6.webp" },
  { id: "15", title: "Drawing Competition", category: "Events", image: "/images/gallery7.webp" },
  { id: "16", title: "Group Discussion", category: "Academics", image: "/images/gallery8.webp" },
]

export function GalleryWithFilters({ items: initialItems, categories: initialCategories }: GalleryWithFiltersProps) {
  const [activeCategory, setActiveCategory] = useState("All")
  // Change state to track selected index instead of object to easily navigate
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const displayItems = initialItems || STATIC_GALLERY_ITEMS

  const allCategories = useMemo(() => {
    if (initialCategories) return ["All", ...initialCategories]
    const cats = Array.from(new Set(displayItems.map(item => item.category)))
    return ["All", ...cats]
  }, [displayItems, initialCategories])

  const filteredItems = useMemo(() => {
    if (activeCategory === "All") return displayItems
    return displayItems.filter(item => item.category === activeCategory)
  }, [displayItems, activeCategory])

  // Get the item using the index safely
  const selectedImage = selectedIndex !== null && filteredItems[selectedIndex] ? filteredItems[selectedIndex] : null

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [selectedImage])

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (selectedIndex === null) return
    setSelectedIndex((prev) => (prev !== null && prev < filteredItems.length - 1 ? prev + 1 : 0))
  }

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (selectedIndex === null) return
    setSelectedIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : filteredItems.length - 1))
  }

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {allCategories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${activeCategory === category
                ? "bg-blue-800 text-white shadow-lg shadow-blue-900/20 scale-105"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 whitespace-normal">
            {filteredItems.map((item, index) => (
              <figure
                key={item.id}
                onClick={() => setSelectedIndex(index)}
                className="relative group rounded-3xl overflow-hidden bg-gray-100 aspect-[4/3] shadow-md hover:shadow-2xl transition-all duration-700 border border-gray-100 cursor-zoom-in"
              >
                <Image
                  src={item.image}
                  alt={item.title}
                  fill
                  className="object-cover transition-transform duration-1000 group-hover:scale-110"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                />

                {/* Overlay with Content */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-x-0 bottom-0 p-6 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="inline-block px-3 py-1 bg-blue-800 text-[10px] text-white font-bold rounded-lg uppercase tracking-widest mb-2 shadow-lg">
                      {item.category}
                    </span>
                    <h3 className="text-white font-bold text-xl leading-snug drop-shadow-md">
                      {item.title}
                    </h3>
                  </div>
                </div>

                {/* Decorative border on hover */}
                <div className="absolute inset-4 border-2 border-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
              </figure>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-200">
            <p className="text-gray-400 font-bold text-lg">No images found in this category.</p>
          </div>
        )}
      </div>

      {/* Lightbox / Zoom Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 p-4 md:p-8"
        >
          {/* Main Containter for Image + Buttons */}
          <div
            className="relative w-full max-w-5xl aspect-[16/9] md:aspect-[16/10] rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 cursor-default bg-black border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Now inside the container to be 'fixed' to the image area */}
            <button
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-blue-600 transition-all border border-white/20 group"
              onClick={(e) => { e.stopPropagation(); setSelectedIndex(null); }}
              title="Close Gallery"
            >
              <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
            </button>

            {/* Navigation Buttons */}
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full bg-black/30 hover:bg-blue-600 text-white backdrop-blur-sm transition-all border border-white/10 group opacity-0 hover:opacity-100 md:opacity-100"
              onClick={handlePrev}
              title="Previous Image"
            >
              <ChevronLeft className="w-8 h-8 group-hover:-translate-x-1 transition-transform" />
            </button>

            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 p-3 rounded-full bg-black/30 hover:bg-blue-600 text-white backdrop-blur-sm transition-all border border-white/10 group opacity-0 hover:opacity-100 md:opacity-100"
              onClick={handleNext}
              title="Next Image"
            >
              <ChevronRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Image */}
            <Image
              src={selectedImage.image}
              alt={selectedImage.title}
              fill
              className="object-contain bg-black/50"
              priority
            />

            {/* Modal Info Bar */}
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              <span className="px-3 py-1 bg-blue-800 text-xs text-white font-bold rounded-lg uppercase tracking-widest mb-3 inline-block">
                {selectedImage.category}
              </span>
              <h4 className="text-white text-2xl md:text-3xl font-bold">{selectedImage.title}</h4>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
